import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Bell, Leaf, Compass, Sparkles, Check, Star, Menu, X, QrCode,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const IMG = {
  hero: "https://images.unsplash.com/photo-1775257796019-3e8db981a1a6",
  lobby: "https://images.pexels.com/photos/14011664/pexels-photo-14011664.jpeg",
  wellness: "https://images.pexels.com/photos/24374825/pexels-photo-24374825.jpeg",
  dining: "https://images.pexels.com/photos/29962487/pexels-photo-29962487.jpeg",
};

const features = [
  { icon: Compass, title: "Concierge", body: "Replace static folders with a unified digital hub. Showcase local guides, house rules and facilities directly on your guest's device." },
  { icon: Leaf, title: "Infrastructure", body: "Eliminate the environmental and financial cost of the paper trap. Support your ESG objectives while deleting printing overheads." },
  { icon: Bell, title: "Engagement", body: "Establish a direct digital bridge through real-time announcements. Alert guests to dining availability and local events instantly." },
  { icon: Sparkles, title: "Efficiency", body: "Centralise service requests and bookings into one frictionless interface. Reduce staff workload and increase secondary revenue." },
];

const testimonials = [
  { name: "Rachel Wrightson", role: "Wellness Club Director", body: "The hub has completely transformed our member engagement. A dedicated digital bridge for class schedules and café updates removed the friction of traditional communication." },
  { name: "Libby Forbes", role: "Lodge & Resort Owner", body: "Replacing static room folders with a live digital concierge was a major operational win. Secondary spend is up and the guest experience is elevated." },
  { name: "Sarah Jennings", role: "Luxury Holiday Let Host", body: "A professional, branded interface for my house rules and recommendations — without the friction of an app. My guests love the premium feel." },
];

function Nav({ content = {} }) {
  const [open, setOpen] = useState(false);
  return (
    <nav className="fixed top-0 inset-x-0 z-50 bg-ink/90 backdrop-blur-xl border-b border-ink">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <span className="text-cream text-xl font-serif tracking-tight">{content.brand_primary || "Guest Relations"}</span>
          <span className="text-gold text-xl font-serif">{content.brand_accent || "Hub"}</span>
        </Link>
        <div className="hidden md:flex items-center gap-10 text-sm text-cream-muted">
          <a href="#products" className="hover:text-cream transition-colors">Products</a>
          <a href="#features" className="hover:text-cream transition-colors">What we do</a>
          <a href="#pricing" className="hover:text-cream transition-colors">Pricing</a>
          <a href="#testimonials" className="hover:text-cream transition-colors">Stories</a>
        </div>
        <div className="hidden md:flex items-center gap-4">
          <Link to="/login" data-testid="nav-login" className="text-sm text-cream-muted hover:text-cream transition-colors">Sign in</Link>
          <Link to="/register" data-testid="nav-register">
            <Button className="rounded-full bg-gold text-black hover:bg-[#D6BB96] px-6">Start free</Button>
          </Link>
        </div>
        <button className="md:hidden text-cream" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-ink border-t border-ink px-6 py-6 flex flex-col gap-4 text-cream-muted">
          <a href="#products" onClick={() => setOpen(false)}>Products</a>
          <a href="#pricing" onClick={() => setOpen(false)}>Pricing</a>
          <Link to="/login">Sign in</Link>
          <Link to="/register"><Button className="rounded-full bg-gold text-black w-full">Start free</Button></Link>
        </div>
      )}
    </nav>
  );
}

export default function Landing() {
  const [plans, setPlans] = useState([]);
  const [yearly, setYearly] = useState(false);
  const [content, setContent] = useState({});

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
    api.get("/public/site-content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (content.seo_title) document.title = content.seo_title;
    if (content.seo_description) {
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
      meta.content = content.seo_description;
    }
  }, [content]);

  return (
    <div className="bg-ink min-h-screen text-cream">
      <Nav content={content} />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={content.hero_image || IMG.hero} alt="Luxury hotel" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-2xl">
            <p className="uppercase tracking-[0.3em] text-xs text-gold mb-6">{content.hero_eyebrow || "Welcome to the future of hospitality"}</p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mb-8">
              {content.hero_title || "The Digital Concierge For Every Host"}
            </h1>
            <p className="text-lg text-cream-muted mb-10 max-w-xl leading-relaxed">
              {content.hero_subtitle || "Launch your own branded guest relations portal in minutes. Replace paper folders with a live digital hub that drives secondary revenue and elevates every stay."}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/register" data-testid="hero-cta">
                <Button className="rounded-full bg-gold text-black hover:bg-[#D6BB96] px-8 h-12 text-base">
                  Build your hub <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <a href="#products">
                <Button variant="outline" className="rounded-full border-cream/30 bg-transparent text-cream hover:bg-cream/10 px-8 h-12 text-base">
                  Explore products
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What we do */}
      <section id="features" className="py-28 border-t border-ink">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-20">
            <p className="uppercase tracking-[0.3em] text-xs text-gold mb-6">What we do</p>
            <h2 className="text-3xl sm:text-4xl leading-tight">
              {content.features_heading || "Bespoke digital infrastructure that empowers every host to streamline operations, enhance the guest experience and drive secondary revenue."}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#232A33]">
            {(content.features || features).map((f, i) => {
              const Icon = features[i % features.length].icon;
              return (
              <motion.div
                key={f.title || i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-ink p-10 hover-lift"
              >
                <Icon className="w-7 h-7 text-gold mb-6" strokeWidth={1.5} />
                <h3 className="text-xl mb-4">{f.title}</h3>
                <p className="text-sm text-cream-muted leading-relaxed">{f.body}</p>
              </motion.div>
            );})}
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="products" className="py-28 bg-ink-surface">
        <div className="max-w-7xl mx-auto px-6 space-y-28">
          {(content.products || []).map((p, i) => (
            <div key={i} className="grid lg:grid-cols-2 gap-16 items-center" data-testid={`product-block-${i}`}>
              <div className={i % 2 === 1 ? "order-1 lg:order-2" : ""}>
                <p className="uppercase tracking-[0.3em] text-xs text-gold mb-6">{p.eyebrow}</p>
                <h2 className="text-4xl mb-8 leading-tight">{p.title}</h2>
                <ul className="space-y-4 text-cream-muted">
                  {(p.bullets || []).map((t) => (
                    <li key={t} className="flex gap-3"><Check className="w-5 h-5 text-gold shrink-0 mt-0.5" /><span>{t}</span></li>
                  ))}
                </ul>
              </div>
              <div className={`rounded-md overflow-hidden hover-lift ${i % 2 === 1 ? "order-2 lg:order-1" : ""}`}>
                <img src={p.image} alt={p.eyebrow} className="w-full h-[420px] object-cover" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="uppercase tracking-[0.3em] text-xs text-gold mb-6">{content.pricing_eyebrow || "Pricing"}</p>
            <h2 className="text-4xl mb-6">{content.pricing_title || "One portal. Every guest."}</h2>
            <div className="inline-flex items-center gap-3 bg-ink-surface border border-ink rounded-full p-1">
              <button onClick={() => setYearly(false)} data-testid="billing-monthly" className={`px-5 py-2 rounded-full text-sm transition-colors ${!yearly ? "bg-gold text-black" : "text-cream-muted"}`}>Monthly</button>
              <button onClick={() => setYearly(true)} data-testid="billing-yearly" className={`px-5 py-2 rounded-full text-sm transition-colors ${yearly ? "bg-gold text-black" : "text-cream-muted"}`}>Yearly · save 2 months</button>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((p) => (
              <div key={p.id} className={`p-8 rounded-md border ${p.popular ? "border-gold bg-ink-surface" : "border-ink bg-ink"} hover-lift flex flex-col`}>
                {p.popular && <span className="self-start text-xs uppercase tracking-widest text-gold mb-4">Most popular</span>}
                <h3 className="text-2xl mb-2">{p.name}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-serif">{p.currency}{yearly ? p.price_yearly : p.price_monthly}</span>
                  <span className="text-cream-muted text-sm">/{yearly ? "year" : "month"}</span>
                </div>
                <ul className="space-y-3 text-sm text-cream-muted mb-8 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-2"><Check className="w-4 h-4 text-gold shrink-0 mt-0.5" />{f}</li>
                  ))}
                </ul>
                <Link to="/register" data-testid={`pricing-cta-${p.id}`}>
                  <Button className={`w-full rounded-full ${p.popular ? "bg-gold text-black hover:bg-[#D6BB96]" : "bg-cream/10 text-cream hover:bg-cream/20"}`}>Get started</Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-28 bg-ink-surface border-y border-ink">
        <div className="max-w-7xl mx-auto px-6">
          <p className="uppercase tracking-[0.3em] text-xs text-gold mb-14 text-center">{content.testimonials_eyebrow || "Testimonials"}</p>
          <div className="grid md:grid-cols-3 gap-8">
            {(content.testimonials || testimonials).map((t, i) => (
              <div key={t.author || t.name || i} className="bg-ink p-8 rounded-md border border-ink">
                <div className="flex gap-1 mb-5">
                  {[...Array(5)].map((_, s) => <Star key={s} className="w-4 h-4 fill-gold text-gold" />)}
                </div>
                <p className="text-cream-muted leading-relaxed mb-6 text-sm">{t.quote || t.body}</p>
                <div>
                  <p className="text-cream">{t.author || t.name}</p>
                  <p className="text-xs text-gold uppercase tracking-widest mt-1">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.dining} alt="Fine dining" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-ink/80" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <QrCode className="w-10 h-10 text-gold mx-auto mb-8" strokeWidth={1.5} />
          <h2 className="text-4xl sm:text-5xl mb-6 leading-tight">Ready to optimise your guest environment?</h2>
          <p className="text-cream-muted mb-10 text-lg">Create your branded hub today and see how digital infrastructure streamlines operations and drives secondary revenue.</p>
          <Link to="/register" data-testid="footer-cta">
            <Button className="rounded-full bg-gold text-black hover:bg-[#D6BB96] px-10 h-12 text-base">Get started today</Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-ink py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-cream-muted">
          <span className="font-serif text-cream">{content.footer_brand || "Guest Relations Hub"}</span>
          <span>{content.footer_tagline || "© 2026 Guest Relations. The digital concierge for every host."}</span>
        </div>
      </footer>
    </div>
  );
}
